import type { Distribution } from "@memorang/types";
import { Image } from "expo-image";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Card, type CardProps, Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import DonutChart from "../DonutChart";
import { Skeleton } from "../Skeleton";
import { StatTile, type StatTileCta } from "../StatTile";
import { ExamDateSubtitle } from "./ExamDateSubtitle";
import { getStatRows } from "./helpers";

export type ExamInfoStat = {
	icon: IconType;
	label: string;
	value: string;
	tooltip?: string;
	skillId?: string;
	cta?: StatTileCta;
	onPress?: () => void;
	tileState?: "known" | "cta" | "no_data";
};

type Props = {
	examIcon?: string;
	examName?: string;
	examDate?: Date;
	examEnabled?: boolean;
	accuracy: number;
	distribution: Distribution;
	xpEarned?: number;
	donutProgressData?: { x: string; y: number }[];
	donutProgressColorScale?: string[];
	allowChangeExamDate?: boolean;
	isLoading?: boolean;
	statsLoading?: boolean;
	toggleShowDatePicker: () => void;
	style?: CardProps["style"];
	stats?: ExamInfoStat[];
	footer?: React.ReactNode;
};

type ExamInfoProgressProps = {
	isLoading: boolean;
	image?: string;
	radius: number;
	avatarSize: number;
	distribution: Distribution;
	donutProgressData?: { x: string; y: number }[];
	donutProgressColorScale?: string[];
};

type ExamInfoLabelsProps = {
	isLoading: boolean;
	isMobile: boolean;
	centered: boolean;
	title: string;
	examEnabled?: boolean;
	examDate?: Date;
	allowChangeExamDate?: boolean;
	toggleShowDatePicker: () => void;
};

type ExamInfoStatsProps = {
	isLoading: boolean;
	statsLoading?: boolean;
	accuracy: number;
	distribution: Distribution;
	examEnabled?: boolean;
	xpEarned?: number;
	stats?: ExamInfoStat[];
};

const ExamInfoProgress = ({
	isLoading,
	image,
	radius,
	distribution,
	donutProgressData,
	donutProgressColorScale,
	avatarSize,
}: ExamInfoProgressProps) => {
	if (isLoading) {
		return <Skeleton size={avatarSize} variant="circle" />;
	}

	return (
		<DonutChart
			distribution={distribution}
			radius={radius}
			colorScale={donutProgressColorScale}
			progressData={donutProgressData}
			innerRadius={radius - 10}
		>
			{image ? (
				<Image
					source={{ uri: image }}
					style={{ width: radius, height: radius }}
					contentFit="contain"
					contentPosition={"center"}
				/>
			) : null}
		</DonutChart>
	);
};

const ExamInfoLabels = ({
	isLoading,
	isMobile,
	centered,
	title,
	examEnabled,
	examDate,
	allowChangeExamDate,
	toggleShowDatePicker,
}: ExamInfoLabelsProps) => {
	const containerProps = centered
		? ({ width: "100%", alignItems: "center", gap: 8 } as const)
		: ({ flex: 1, gap: 6 } as const);

	if (isLoading) {
		return (
			<Box {...containerProps} style={{ minWidth: 0 }}>
				<Skeleton
					width={centered ? "60%" : "50%"}
					height={isMobile ? 24 : 28}
				/>
				{examEnabled ? (
					<Skeleton width={centered ? "45%" : "60%"} height={24} />
				) : null}
			</Box>
		);
	}

	return (
		<Box {...containerProps} style={{ minWidth: 0 }}>
			<Text
				variant={isMobile ? "titleMedium" : "titleLarge"}
				numberOfLines={1}
				ellipsizeMode="tail"
				style={{
					width: "100%",
					maxWidth: "100%",
					textAlign: centered ? "center" : "left",
				}}
			>
				{title}
			</Text>
			{examEnabled ? (
				<ExamDateSubtitle
					examDate={examDate}
					allowChangeExamDate={allowChangeExamDate}
					toggleShowDatePicker={toggleShowDatePicker}
				/>
			) : null}
		</Box>
	);
};

const statColumnProps = {
	flex: 1,
	minWidth: 0,
	alignSelf: "stretch",
	style: { flexBasis: 0 },
} as const;

const ExamInfoStats = ({
	isLoading,
	statsLoading,
	accuracy,
	distribution,
	examEnabled,
	xpEarned,
	stats,
}: ExamInfoStatsProps) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	const numCompleted =
		Object.values(distribution).reduce((a, b) => a + b, 0) -
		(distribution.none || 0);

	const tiles = stats
		? stats.map((stat) => {
				const tile = (
					<StatTile
						key={stat.label}
						icon={stat.icon}
						label={stat.label}
						value={stat.value}
						tooltip={stat.tooltip}
						cta={stat.cta}
						onPress={stat.onPress}
						isLoading={isLoading || statsLoading}
					/>
				);

				return {
					key: stat.label,
					node: tile,
				};
			})
		: [
				{
					key: "accuracy",
					node: (
						<StatTile
							icon="brain"
							label={t("dashboard.stats.accuracy")}
							value={t("dashboard.stats.accuracyValue", { value: accuracy })}
							isLoading={isLoading}
						/>
					),
				},
				{
					key: "completed",
					node: (
						<StatTile
							icon="card-multiple-outline"
							label={t("dashboard.stats.completed", {
								count: numCompleted,
							})}
							value={t("dashboard.stats.completedValue", {
								count: numCompleted,
							})}
							isLoading={isLoading}
						/>
					),
				},
				...(examEnabled
					? []
					: [
							{
								key: "xpEarned",
								node: (
									<StatTile
										icon="trophy-variant-outline"
										label={t("dashboard.stats.xpEarned")}
										value={t("dashboard.stats.xpEarnedValue", {
											value: xpEarned ?? 0,
										})}
										isLoading={isLoading}
									/>
								),
							},
						]),
			];

	return (
		<Box
			gap={8}
			width="100%"
			minWidth={0}
			alignSelf="stretch"
			style={{
				flexGrow: isMobile ? 0 : 1,
				flexShrink: 1,
				flexBasis: isMobile ? "auto" : 0,
			}}
		>
			{getStatRows(tiles).map((row) => (
				<Box
					key={row[0].key}
					flexGrow={isMobile ? 0 : 1}
					flexDirection="row"
					gap={8}
					minWidth={0}
					alignItems="stretch"
					alignSelf="stretch"
					style={{ flexBasis: "auto" }}
				>
					{row.map(({ key, node }) => (
						<Box key={key} {...statColumnProps}>
							{node}
						</Box>
					))}
				</Box>
			))}
		</Box>
	);
};

export const ExamInfoCard = ({
	examIcon,
	examName,
	examDate,
	examEnabled = true,
	accuracy,
	distribution,
	xpEarned,
	donutProgressData,
	donutProgressColorScale,
	allowChangeExamDate,
	isLoading = false,
	statsLoading,
	toggleShowDatePicker,
	style,
	stats,
	footer,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	const avatarSize = isMobile ? 72 : 90;
	const radius = avatarSize / 2;

	return (
		<FlatCard
			style={{
				borderRadius: isMobile ? 0 : theme.roundness * 3,
				...StyleSheet.flatten(style),
			}}
		>
			<Card.Content
				style={{ paddingHorizontal: isMobile ? 16 : 24, paddingVertical: 24 }}
			>
				<Box gap={isMobile ? 16 : 24}>
					<Box
						width={"100%"}
						flexDirection={isMobile ? "column" : "row"}
						gap={isMobile ? 16 : 24}
						justifyContent="space-between"
						alignItems="stretch"
					>
						<Box
							flex={isMobile ? undefined : 1}
							width={isMobile ? "100%" : undefined}
							flexDirection={isMobile ? "column" : "row"}
							alignItems="center"
							gap={isMobile ? 8 : 16}
							minWidth={0}
							minHeight={avatarSize}
						>
							<ExamInfoProgress
								isLoading={isLoading}
								image={examIcon}
								radius={radius}
								avatarSize={avatarSize}
								distribution={distribution}
								donutProgressData={donutProgressData}
								donutProgressColorScale={donutProgressColorScale}
							/>
							<ExamInfoLabels
								isLoading={isLoading}
								isMobile={isMobile}
								centered={isMobile}
								title={examName ?? ""}
								examEnabled={examEnabled}
								examDate={examDate}
								allowChangeExamDate={allowChangeExamDate}
								toggleShowDatePicker={toggleShowDatePicker}
							/>
						</Box>
						<ExamInfoStats
							isLoading={isLoading}
							statsLoading={statsLoading}
							accuracy={accuracy}
							distribution={distribution}
							examEnabled={examEnabled}
							xpEarned={xpEarned}
							stats={stats}
						/>
					</Box>
					{footer}
				</Box>
			</Card.Content>
		</FlatCard>
	);
};
